import { DeleteOutline } from '@mui/icons-material'
import {
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tooltip,
} from '@mui/material'
import type { ResponseDatetime } from '@ulysses-inc/harami_api_client'
import { ResponseDatetimeRuleSubTypeEnum } from '@ulysses-inc/harami_api_client'
import { type FC, useId } from 'react'

import { isNullish } from 'src/util/isNullish'
import {
  DatetimeRuleContainer,
  DatetimeRuleDayText,
  DatetimeRuleDescription,
  DatetimeRuleInput,
  RuleLabel,
} from './dumb'

interface Props {
  responseDatetimes: ResponseDatetime[]
  updateTemplateNode: (responseDatetime: ResponseDatetime) => void
}

enum RuleValueSign {
  MINUS = -1,
  PLUS = 1,
  NONE = 0,
}

/**
 * 0または正の整数が入力されていた場合のみ、その数値を返し、
 * それ以外が入力されていた場合はundefinedを返す
 *
 * TestのためにExportしている
 *
 * @param inputValue
 * @returns
 */
export const convertOnlyValidInput = (
  inputValue: string | number | null | undefined,
): number | undefined => {
  if (isNullish(inputValue)) {
    return undefined
  }
  const inputNumber = Number(inputValue)
  if (Number.isNaN(inputNumber)) {
    return undefined
  }

  if (inputNumber < 0) {
    return undefined
  }

  if (!Number.isInteger(inputNumber)) {
    return undefined
  }

  return inputNumber
}

/**
 * ルール設定の下部に表示する説明文を生成する
 * 有効なルール設定がなされていない場合、undefinedを返す
 *
 * TestのためにExportしている
 *
 * @param signedRuleValue
 * @param subType
 * @returns
 */
export const makeHintText = (
  signedRuleValue: number | undefined,
  subType: ResponseDatetimeRuleSubTypeEnum | undefined,
): string | undefined => {
  if (isNullish(signedRuleValue)) {
    return undefined
  }
  const defaultSubType =
    subType || ResponseDatetimeRuleSubTypeEnum.LESS_THAN_OR_EQUAL
  const absInputValue = Math.abs(signedRuleValue)
  switch (defaultSubType) {
    case ResponseDatetimeRuleSubTypeEnum.GREATER_THAN_OR_EQUAL: {
      if (signedRuleValue === 0) {
        return `記録日の日付を含んだ、さらに未来の期間を指します。`
      }
      if (signedRuleValue < 0) {
        return `記録日の日付から、${absInputValue}日前の日付を含んだ、さらに過去の期間を指します。`
      }
      return `記録日の日付から、${absInputValue}日後の日付を含んだ、さらに未来の期間を指します。`
    }
    case ResponseDatetimeRuleSubTypeEnum.LESS_THAN_OR_EQUAL: {
      if (signedRuleValue === 0) {
        return `記録日の日付を指します。`
      }
      if (signedRuleValue < 0) {
        return `記録日の日付から、${absInputValue}日前までの範囲の日付を指します。`
      }
      return `記録日の日付から、${absInputValue}日後までの範囲の日付を指します。`
    }
    case ResponseDatetimeRuleSubTypeEnum.EQUAL: {
      if (signedRuleValue === 0) {
        return `記録日の日付を指します。`
      }
      if (signedRuleValue < 0) {
        return `記録日の日付から、${absInputValue}日前の日付を指します。`
      }
      return `記録日の日付から、${absInputValue}日後の日付を指します。`
    }
    default:
      return undefined
  }
}

export const DatetimeRule: FC<Props> = ({
  responseDatetimes,
  updateTemplateNode,
}) => {
  // stateには、符号付で日数を格納する。（その値を良い感じに表示するのはUIコンポーネントの責務である）
  // X日 前 -> マイナス
  // X日 後 -> プラス
  const singedRuleValue = responseDatetimes[0]?.rule?.value
  const unsignedRuleValue = singedRuleValue && Math.abs(singedRuleValue)
  const ruleValueSign = !singedRuleValue
    ? RuleValueSign.NONE
    : singedRuleValue < 0
      ? RuleValueSign.MINUS
      : RuleValueSign.PLUS

  const ruleDescription = makeHintText(
    singedRuleValue,
    responseDatetimes[0]?.rule?.subType,
  )

  const ruleLabelId = useId()

  return (
    <DatetimeRuleContainer>
      <div>
        <RuleLabel id={ruleLabelId} style={{ marginRight: 8 }}>
          ルール設定:
        </RuleLabel>
        <DatetimeRuleInput
          value={unsignedRuleValue}
          onChange={inputValue => {
            const validValue = convertOnlyValidInput(inputValue)
            // 既存の符号（前/後）を維持する。「前」選択中ならマイナスで保存する
            const signedValue =
              validValue !== undefined && ruleValueSign === RuleValueSign.MINUS
                ? -validValue
                : validValue
            const newResponseDateTime: ResponseDatetime = {
              ...responseDatetimes[0],
              rule: {
                ...responseDatetimes[0]?.rule,
                value: signedValue,
                subType: responseDatetimes[0]?.rule?.subType
                  ? responseDatetimes[0]?.rule.subType
                  : ResponseDatetimeRuleSubTypeEnum.LESS_THAN_OR_EQUAL,
              },
            }
            updateTemplateNode(newResponseDateTime)
          }}
          aria-labelledby={ruleLabelId}
        />
        <DatetimeRuleDayText>{'日'}</DatetimeRuleDayText>
        <Select<number>
          value={ruleValueSign}
          sx={{ width: 64, ml: 1 }}
          disabled={ruleValueSign === RuleValueSign.NONE}
          onChange={(e: SelectChangeEvent<number>) => {
            const newSign = e.target.value as RuleValueSign
            const inputValue = responseDatetimes[0]?.rule?.value
            const signedInputValue =
              inputValue && Math.abs(inputValue) * newSign
            const newResponseDateTime: ResponseDatetime = {
              ...responseDatetimes[0],
              rule: {
                ...responseDatetimes[0]?.rule,
                value: signedInputValue,
              },
            }
            updateTemplateNode(newResponseDateTime)
          }}
          renderValue={val =>
            val === RuleValueSign.NONE
              ? ''
              : val === RuleValueSign.MINUS
                ? '前'
                : '後'
          }
          size="small"
          data-testid="datetime-rule-sign-select"
        >
          <MenuItem value={RuleValueSign.NONE} sx={{ display: 'none' }} />
          <MenuItem
            value={RuleValueSign.MINUS}
            data-testid="datetime-rule-sign-select-item"
          >
            前
          </MenuItem>
          <MenuItem
            value={RuleValueSign.PLUS}
            data-testid="datetime-rule-sign-select-item"
          >
            後
          </MenuItem>
        </Select>
        <Select<number>
          value={
            responseDatetimes[0]?.rule?.subType ??
            ResponseDatetimeRuleSubTypeEnum.LESS_THAN_OR_EQUAL
          }
          sx={{ width: 140, ml: 1 }}
          onChange={(e: SelectChangeEvent<number>) => {
            const changeType = e.target.value as ResponseDatetimeRuleSubTypeEnum
            const newResponseDateTime: ResponseDatetime = {
              ...responseDatetimes[0],
              rule: {
                ...responseDatetimes[0]?.rule,
                subType: changeType,
              },
            }
            updateTemplateNode(newResponseDateTime)
          }}
          size="small"
          data-testid="datetime-rule-sub-type-enum-select"
        >
          <MenuItem
            value={ResponseDatetimeRuleSubTypeEnum.LESS_THAN_OR_EQUAL}
            data-testid="datetime-rule-sub-type-enum-select-item"
          >
            以内の日付
          </MenuItem>
          <MenuItem
            value={ResponseDatetimeRuleSubTypeEnum.GREATER_THAN_OR_EQUAL}
            data-testid="datetime-rule-sub-type-enum-select-item"
          >
            以上の日付
          </MenuItem>
          <MenuItem
            value={ResponseDatetimeRuleSubTypeEnum.EQUAL}
            data-testid="datetime-rule-sub-type-enum-select-item"
          >
            と同じ日付
          </MenuItem>
        </Select>
        <Tooltip title="ルール設定をクリア">
          <IconButton
            color="error"
            size="small"
            onClick={() => {
              const newResponseDateTime: ResponseDatetime = {
                ...responseDatetimes[0],
                rule: undefined,
              }
              updateTemplateNode(newResponseDateTime)
            }}
            sx={{ ml: 0.5 }}
            aria-label="ルール設定をクリア"
          >
            <DeleteOutline fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      </div>
      {ruleDescription && (
        <DatetimeRuleDescription>{ruleDescription}</DatetimeRuleDescription>
      )}
    </DatetimeRuleContainer>
  )
}
